// src/pages/Auth.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { useAuth } from "../lib/AuthContext";
import { auth } from "../lib/firebase";
import RoleSelector from "../components/RoleSelector";
import linkedInIcon from "../LinkedIn_logo_initials.png";

const ACCOUNT_EXISTS_GOOGLE_MSG =
  "An account with this email already exists using Google Sign-In. Please log in with Google.";

function linkedInErrorMessage(err) {
  if (!err || err.code === "auth/popup-closed-by-user") return null;
  if (err.code === "auth/account-exists-with-different-credential") {
    return ACCOUNT_EXISTS_GOOGLE_MSG;
  }
  return "LinkedIn sign-in failed. Please try again.";
}

function AuthToast({ message, onDismiss }) {
  useEffect(() => {
    if (!message) return undefined;
    const t = setTimeout(() => onDismiss?.(), 6000);
    return () => clearTimeout(t);
  }, [message]);

  if (!message) return null;

  return (
    <div className="auth-toast" role="status" aria-live="polite">
      <span className="auth-toast-dot" aria-hidden="true" />
      <span>{message}</span>
      <button type="button" className="auth-toast-close" onClick={onDismiss} aria-label="Dismiss">
        ×
      </button>
    </div>
  );
}

export function SignupPage() {
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "corporation" ? "corporation" : "startup";
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ email: "", password: "", displayName: "", orgName: "" });
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [linkedinLoading, setLinkedinLoading] = useState(false);
  const [needsOrgName, setNeedsOrgName] = useState(false);
  const [pendingGoogleRole, setPendingGoogleRole] = useState(null);
  const [orgNameInput, setOrgNameInput] = useState("");
  const { signup, loginWithGoogle, loginWithLinkedIn } = useAuth();
  const navigate = useNavigate();

  async function handleLinkedInSignup() {
    setError("");
    setToast("");
    setLinkedinLoading(true);
    try {
      await loginWithLinkedIn(role, "");
    } catch (err) {
      const msg = linkedInErrorMessage(err) || err.message;
      if (msg === ACCOUNT_EXISTS_GOOGLE_MSG) setToast(msg);
      else if (msg) setError(msg);
      setLinkedinLoading(false);
    }
  }

  async function handleGoogleSignup() {
    setError(""); setGoogleLoading(true);
    try {
      const { isNewUser, needsOnboarding } = await loginWithGoogle(role, "");
      if (needsOnboarding) {
        navigate("/complete-profile", { replace: true });
        return;
      }
      if (isNewUser) {
        setPendingGoogleRole(role);
        setNeedsOrgName(true);
      } else {
        navigate("/");
      }
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user") {
        setError("Google sign-in failed. Please try again.");
      }
    } finally {
      setGoogleLoading(false);
    }
  }

  async function handleOrgNameSubmit(e) {
    e.preventDefault();
    if (!orgNameInput.trim()) return;
    const { updateDoc, doc } = await import("firebase/firestore");
    const { db, auth: firebaseAuth } = await import("../lib/firebase");
    await updateDoc(doc(db, "users", firebaseAuth.currentUser.uid), { orgName: orgNameInput.trim() });
    navigate("/");
  }

  function set(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await signup(form.email, form.password, form.displayName, role, form.orgName);
      navigate("/");
    } catch (err) {
      const msg = err.message || "";
      if (msg.includes("email-already-in-use")) setError("An account with this email already exists. Try signing in.");
      else if (msg.includes("weak-password")) setError("Password must be at least 8 characters.");
      else if (msg.includes("network-request-failed")) setError("Network error — check your internet connection.");
      else setError(msg.replace("Firebase: ", "").replace(/\s*\(auth\/[^)]*\)\.?/g, "") || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (needsOrgName) {
    return (
      <AuthLayout title="One more thing" subtitle="Tell us your organization name to finish setting up your account">
        <AuthToast message={toast} onDismiss={() => setToast("")} />
        <form onSubmit={handleOrgNameSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="form-group">
            <label className="form-label" htmlFor="signup-org">
              {pendingGoogleRole === "corporation" ? "Company name" : "Startup name"}{" "}
              <span className="form-required">*</span>
            </label>
            <input
              id="signup-org"
              className="input"
              value={orgNameInput}
              onChange={e => setOrgNameInput(e.target.value)}
              placeholder={pendingGoogleRole === "corporation" ? "Acme Corp" : "My Startup Inc"}
              autoComplete="organization"
              autoFocus
              required
              style={{ minHeight: 44 }}
            />
          </div>
          <button className="btn btn-primary" type="submit" style={{ justifyContent: "center", padding: "12px", minHeight: 48 }}>
            Continue →
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Create your account" subtitle={<>Already have an account? <Link to="/login" style={{ color: "var(--teal)" }}>Sign in</Link></>}>
      <AuthToast message={toast} onDismiss={() => setToast("")} />

      <div className="form-group" style={{ marginBottom: 18 }}>
        <label className="form-label" id="signup-role-label">
          I am a… <span className="form-required">*</span>
        </label>
        <RoleSelector value={role} onChange={setRole} name="signup-role" />
      </div>

      <button type="button" onClick={handleLinkedInSignup} disabled={linkedinLoading || googleLoading} className="oauth-btn" style={{ marginBottom: 10 }}>
        {linkedinLoading ? (
          <span className="spinner" style={{ width: 16, height: 16 }} />
        ) : (
          <>
            <LinkedInIcon />
            Continue with LinkedIn
          </>
        )}
      </button>

      <button type="button" onClick={handleGoogleSignup} disabled={googleLoading || linkedinLoading} className="oauth-btn" style={{ marginBottom: 18 }}>
        {googleLoading ? (
          <span className="spinner" style={{ width: 16, height: 16 }} />
        ) : (
          <>
            <GoogleIcon />
            Continue with Google
          </>
        )}
      </button>

      <div className="auth-divider">
        <span>or sign up with email</span>
      </div>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="form-group">
          <label className="form-label" htmlFor="signup-name">Full name</label>
          <input id="signup-name" className="input" value={form.displayName} onChange={set("displayName")} placeholder="Jane Smith" autoComplete="name" required style={{ minHeight: 44 }} />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="signup-org-email">
            {role === "corporation" ? "Company name" : "Startup name"}
          </label>
          <input
            id="signup-org-email"
            className="input"
            value={form.orgName}
            onChange={set("orgName")}
            placeholder={role === "corporation" ? "Acme Corp" : "My Startup Inc"}
            autoComplete="organization"
            required
            style={{ minHeight: 44 }}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="signup-email">Work email</label>
          <input id="signup-email" className="input" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" autoComplete="email" required style={{ minHeight: 44 }} />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="signup-password">Password</label>
          <input id="signup-password" className="input" type="password" value={form.password} onChange={set("password")} placeholder="Min. 8 characters" minLength={8} autoComplete="new-password" required style={{ minHeight: 44 }} />
        </div>

        {error && (
          <div className="form-error" role="alert" style={{ padding: "8px 12px", background: "rgba(220,80,60,0.1)", borderRadius: "var(--radius)", border: "1px solid rgba(220,80,60,0.2)" }}>
            {error}
          </div>
        )}

        <button className="btn btn-primary" type="submit" disabled={loading}
          style={{ marginTop: 4, justifyContent: "center", padding: "12px", minHeight: 48 }}>
          {loading ? <span className="spinner" style={{ width: 16, height: 16 }} />
            : `Create ${role === "corporation" ? "business owner" : "startup"} account →`}
        </button>
      </form>

      <p style={{ fontSize: 12, color: "var(--text-3)", textAlign: "center", marginTop: 16, lineHeight: 1.6 }}>
        By signing up you agree to our Terms of Service and{" "}
        <a
          href="https://manacommon.com/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "var(--text-2)", textDecoration: "underline" }}
        >
          Privacy Policy
        </a>
        .
      </p>
    </AuthLayout>
  );
}

export function LoginPage() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [linkedinLoading, setLinkedinLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const { login, loginWithGoogle, loginWithLinkedIn } = useAuth();
  const navigate = useNavigate();

  function set(field) { return e => setForm(f => ({ ...f, [field]: e.target.value })); }

  function afterOAuth({ needsOnboarding }) {
    if (needsOnboarding) navigate("/complete-profile", { replace: true });
    else navigate("/", { replace: true });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/");
    } catch (err) {
      setError("Incorrect email or password.");
    } finally {
      setLoading(false);
    }
  }

  async function handleLinkedInLogin() {
    setError("");
    setToast("");
    setLinkedinLoading(true);
    try {
      // Role deferred — new users land on /complete-profile after LinkedIn
      await loginWithLinkedIn(null, "");
    } catch (err) {
      const msg = linkedInErrorMessage(err) || err.message;
      if (msg === ACCOUNT_EXISTS_GOOGLE_MSG) setToast(msg);
      else if (msg) setError(msg);
      setLinkedinLoading(false);
    }
  }

  async function handleGoogleLogin() {
    setError(""); setGoogleLoading(true);
    try {
      const outcome = await loginWithGoogle(null, "");
      afterOAuth(outcome);
    } catch (err) {
      if (err.code !== "auth/popup-closed-by-user") {
        setError("Google sign-in failed. Please try again.");
      }
    } finally {
      setGoogleLoading(false);
    }
  }

  async function handleForgotPassword() {
    const email = form.email.trim();
    if (!email) {
      setError("Please enter your email address first.");
      setResetSent(false);
      return;
    }
    setError("");
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      setResetSent(false);
      setError(err.message || "Failed to send password reset email. Please try again.");
    }
  }

  return (
    <AuthLayout title="Welcome back" subtitle={<>New to Mana Tech Access? <Link to="/signup" style={{ color: "var(--teal)" }}>Create an account</Link></>}>
      <AuthToast message={toast} onDismiss={() => setToast("")} />
      <button type="button" onClick={handleLinkedInLogin} disabled={linkedinLoading || googleLoading} className="oauth-btn" style={{ marginBottom: 10 }}>
        {linkedinLoading ? (
          <span className="spinner" style={{ width: 16, height: 16 }} />
        ) : (
          <>
            <LinkedInIcon />
            Sign In with LinkedIn
          </>
        )}
      </button>

      <button type="button" onClick={handleGoogleLogin} disabled={googleLoading || linkedinLoading} className="oauth-btn" style={{ marginBottom: 18 }}>
        {googleLoading ? (
          <span className="spinner" style={{ width: 16, height: 16 }} />
        ) : (
          <>
            <GoogleIcon />
            Continue with Google
          </>
        )}
      </button>

      <div className="auth-divider">
        <span>or sign in with email</span>
      </div>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div className="form-group">
          <label className="form-label" htmlFor="login-email">Email</label>
          <input
            id="login-email"
            className="input"
            type="email"
            value={form.email}
            onChange={e => {
              setResetSent(false);
              set("email")(e);
            }}
            autoComplete="email"
            required
            autoFocus
            style={{ minHeight: 44 }}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="login-password">Password</label>
          <input id="login-password" className="input" type="password" value={form.password} onChange={set("password")} autoComplete="current-password" required style={{ minHeight: 44 }} />
        </div>
        {error && (
          <div className="form-error" role="alert" style={{ padding: "8px 12px", background: "rgba(220,80,60,0.1)", borderRadius: "var(--radius)", border: "1px solid rgba(220,80,60,0.2)" }}>
            {error}
          </div>
        )}
        <button className="btn btn-primary" type="submit" disabled={loading}
          style={{ marginTop: 4, justifyContent: "center", padding: "12px", minHeight: 48 }}>
          {loading ? <span className="spinner" style={{ width: 16, height: 16 }} /> : "Sign in →"}
        </button>
      </form>

      <div style={{ textAlign: "center", marginTop: 14 }}>
        <button
          type="button"
          onClick={handleForgotPassword}
          style={{
            background: "none", border: "none", padding: 0,
            fontSize: 13, color: "var(--text-3)", cursor: "pointer",
            textDecoration: "underline", minHeight: 44
          }}
        >
          Forgot Password
        </button>
        <p style={{
          margin: "10px 0 0",
          fontSize: 12,
          lineHeight: 1.45,
          color: "var(--text-3)"
        }}>
          Didn&apos;t receive it? Please check your Spam / Junk folder.
        </p>
      </div>

      {resetSent && (
        <div
          role="status"
          aria-live="polite"
          style={{
            marginTop: 14,
            padding: "12px 14px",
            borderRadius: "var(--radius)",
            background: "rgba(117, 250, 170, 0.1)",
            border: "1px solid rgba(117, 250, 170, 0.28)",
            textAlign: "left"
          }}
        >
          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "var(--text)" }}>
            Password reset email sent. Check your inbox.
          </p>
          <p style={{ margin: "8px 0 0", fontSize: 12, lineHeight: 1.45, color: "var(--text-2)" }}>
            Didn&apos;t receive it? Please check your Spam / Junk folder.
          </p>
        </div>
      )}
    </AuthLayout>
  );
}

function LinkedInIcon() {
  return (
    <img
      src={linkedInIcon}
      alt=""
      width={18}
      height={18}
      style={{ display: "block", objectFit: "contain" }}
    />
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
      <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
      <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
      <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
    </svg>
  );
}

function AuthLayout({ title, subtitle, children }) {
  return (
    <div className="auth-page">
      <div className="auth-page-inner">
        <div className="auth-page-header">
          <h1>{title}</h1>
          <p>{subtitle}</p>
        </div>
        <div className="card auth-card">
          {children}
        </div>
      </div>
    </div>
  );
}
